import gql from 'graphql-tag';

import { apolloProvider } from '../../../vueApollo';
import { MUT_ERR_CONTACTS, MUT_GET_CONTACTS, MUT_SET_CONTACTS } from './types';

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

const state = () => ({
  contacts: undefined,
  isLoading: null,
});

const getters = {
  CONTACTS: (state) => {
    return state.contacts;
  },
  ENABLED_CONTACTS: (state) => {
    if (state.contacts) {
      return state.contacts.filter((c) => c.enabled === true);
    } else {
      return [];
    }
  },
  CONTACTS_ISLOADING: (state) => {
    return state.isLoading;
  },
};

// Sequence number of the most recent request; see the action.
let latestRequest = 0;
// The request still running, if any, so a second caller for the same org can join it.
let inFlight = null;

const actions = {
  /**
   * Accepts an org id, or `{ orgId, force }`. For large orgs this is a 16MB+ unpaginated
   * download, and org boot plus every view that mounts during it ask for it — so a
   * request for the org already being fetched joins that one instead of starting
   * another. Pass `force: true` after a write, when a request that began before it
   * would come back without it.
   */
  getContacts(context, payload) {
    const { orgId, force = false } = typeof payload === 'object' && payload !== null ? payload : { orgId: payload };
    if (!force && inFlight && inFlight.orgId === orgId) return inFlight.promise;
    const promise = fetchContacts(context, orgId).finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
    inFlight = { orgId, promise };
    return promise;
  },
};

async function fetchContacts({ commit, rootState }, orgId) {
  const client = apolloProvider.clients.defaultClient;
  // Requests overlap when the org changes mid-flight (the user switches, or the v3 shell
  // corrects the org this window booted on). The NEWEST request owns this module's data
  // and loading flag: an older response never touches them, and the newest one only
  // lands if its org is still current — otherwise it clears rather than leaves another
  // org's data on screen. CIB-4184.
  const request = ++latestRequest;
  const isLatest = () => request === latestRequest;
  const stillCurrent = () => rootState.currentOrg?.id === orgId;
  commit(MUT_GET_CONTACTS);
  try {
    const res = await client.query({
      query: gql`
        query GetContacts($orgId: ID!) {
          contacts(orgId: $orgId, includeDisabled: true) {
            id
            source
            name
            enabled
            type
            metadata
            addresses {
              coin
              address
              memo
              defaultExpenseCategoryId
              defaultRevenueCategoryId
              networkId
            }
            accountingConnectionId
            defaultExpenseCategoryId
            defaultRevenueCategoryId
          }
        }
      `,
      variables: {
        orgId,
      },
      fetchPolicy: 'network-only',
    });
    if (!isLatest()) return;
    if (!stillCurrent()) {
      commit(MUT_ERR_CONTACTS);
      return;
    }
    commit(MUT_SET_CONTACTS, res.data.contacts);
  } catch (e) {
    if (!isLatest()) return;
    commit(MUT_ERR_CONTACTS);
  }
}

const mutations = {
  [MUT_GET_CONTACTS](state) {
    state.isLoading = true;
  },
  [MUT_SET_CONTACTS](state, contacts) {
    // name A–Z, so every picker reading this getter lists them that way
    state.contacts = contacts
      ? [...contacts].sort((a, b) => nameCollator.compare(a.name ?? '', b.name ?? ''))
      : contacts;
    state.isLoading = false;
  },
  [MUT_ERR_CONTACTS](state) {
    state.contacts = undefined;
    state.isLoading = false;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
